import { useEffect, useState, useCallback } from 'react';
import { apiService } from '@services/api';
import logger from '@utils/logger';
import { NOTIFICATION_BADGE_REFRESH_EVENT } from '@utils/notificationBadgeBus';

export interface NotificationBadgeCountsForClient {
  openCount: number | null;
  inProgressCount: number | null;
  overdueCount: number | null;
  pendingSuggestionsCount: number | null;
  pendingChecklistsCount: number | null;
  patrimonyDivergenceCount: number | null;
  total: number;
}

/** Mapa esparso: só os condomínios com pelo menos 1 pendência aparecem como chave. */
export type NotificationBadgeCountsByClient = Record<string, NotificationBadgeCountsForClient>;

const REFETCH_INTERVAL_MS = 60_000;

/**
 * Pendências POR condomínio (pedido do usuário, 10/09): "preciso de uma forma clara para o
 * usuário saber que tem pendências em um condomínio que não está filtrado no momento" — o sino
 * (`useNotificationBadgeCounts.ts`) já é escopado ao condomínio filtrado, então sozinho ele não
 * avisa sobre pendência em OUTRO condomínio do portfólio. Este hook alimenta o indicador no
 * seletor de condomínio: uma bolinha sempre visível no botão fechado (sem precisar abrir nada)
 * quando existe pendência em algum condomínio que não é o selecionado no momento, e o
 * detalhamento por condomínio (as mesmas 6 categorias do sino) ao abrir o dropdown.
 *
 * Mesmo padrão useEffect+useState de `useNotificationBadgeCounts.ts` (sem react-query no web) —
 * refetch periódico leve + em foco de janela + imediato quando uma mutação relevante acontece
 * (mesmo barramento `NOTIFICATION_BADGE_REFRESH_EVENT` usado pelo sino).
 */
export function useNotificationBadgeCountsByClient() {
  const [countsByClient, setCountsByClient] = useState<NotificationBadgeCountsByClient | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCounts = useCallback(async () => {
    try {
      const response = await apiService.getNotificationBadgeCountsByClient();
      if (response.data.success) {
        setCountsByClient(response.data.data);
        setError(null);
      } else {
        setError('Não foi possível carregar as pendências por condomínio.');
      }
    } catch (err) {
      logger.error('Erro ao buscar pendências por condomínio', err);
      setError('Não foi possível carregar as pendências por condomínio.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCounts();
    const interval = setInterval(fetchCounts, REFETCH_INTERVAL_MS);
    const onFocus = () => fetchCounts();
    const onRelevantMutation = () => fetchCounts();
    window.addEventListener('focus', onFocus);
    window.addEventListener(NOTIFICATION_BADGE_REFRESH_EVENT, onRelevantMutation);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(NOTIFICATION_BADGE_REFRESH_EVENT, onRelevantMutation);
    };
  }, [fetchCounts]);

  return { countsByClient, loading, error, refetch: fetchCounts };
}
